import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12 } from "styled-components";
import type { SanityPageBuilderQuoteSection, SanityPageBuilderSection } from "@ouster/shared";
import type { SVGProps } from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { SanityRichText } from "~/features/sanity/rich-text";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderQuoteSection;
  };
};
type QuoteIconProps = SVGProps<SVGSVGElement> & {
  mode: Mode;
};
const QuoteIcon = ({
  mode,
  ...rest
}: QuoteIconProps) => {
  const fill = mode === "light" ? {
    "fill": "#D0D0DA"
  } : {
    "fill": "#B4B4BD"
  };
  return <svg width="36" height="32" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg" {...rest}>
      <_StyledPath d="M20.76 0H36V14.7416L27.36 32H21.72L27.84 14.7416H20.76V0ZM0 0H15.24V14.7416L6.6 32H0.959999L7.08 14.7416H0V0Z" $_css={fill} />
    </svg>;
};
var _StyledQuoteIcon2 = /*#__PURE__*/_styled12(QuoteIcon).withConfig({
  displayName: "quote___StyledQuoteIcon2",
  componentId: "sc-7806bf-0"
})({
  "position": "absolute",
  "bottom": "0",
  "right": "0"
});
var _StyledQuoteIcon = _styled7(QuoteIcon).withConfig({
  componentId: "sc-sf-197vnxl-0"
})({
  "marginBottom": "2.4rem",
  "--tw-rotate": "180deg",
  "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
});
export function SanityQuoteSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  if (!sectionContent.quoteText) {
    return null;
  }
  const mode = sectionSettings?.mode || "light";
  const theme = sectionSettings?.theme || "white";
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });

  // v3 uses true white for the "white" theme; the block previously hardcoded grey.
  // Use mode-aware bg-primary so a dark-mode quote (white text) stays legible.
  const surface = theme as Theme === "grey" ? {
    "backgroundColor": "var(--bg-secondary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  };
  return <_StyledSection data-theme={mode} id={sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key} data-sanity-section-key={section._key} data-sanity-section-type={section._type} data-anchor-name={sectionSettings?.anchorName} $_css2={[{
    "color": "var(--text-primary)"
  }, surface, sectionSettings?.hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <_StyledWrap>
        <_StyledDiv>
          <_StyledFigure $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "paddingLeft": "var(--spacing-xl)",
          "paddingRight": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-xl)",
          "paddingBottom": "var(--spacing-xl)",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px",
            "borderColor": "var(--stroke-primary)",
            "paddingLeft": "var(--spacing-4xl)",
            "paddingRight": "var(--spacing-4xl)",
            "paddingTop": "var(--spacing-3xl)",
            "paddingBottom": "var(--spacing-3xl)"
          }
        }, surface]}>
            <_StyledBlockquote>
              <_StyledQuoteIcon mode={mode as Mode} />
              <_StyledSanityRichText value={sectionContent.quoteText} $_css4={[{
              "fontSize": "2.2rem",
              "lineHeight": "1.2",
              "fontWeight": "400",
              "@media (min-width: 1024px)": {
                "fontSize": "3.2rem",
                "lineHeight": "1.2",
                "letterSpacing": "0.032rem",
                "fontWeight": "400"
              }
            }, themeStyle.text]} />
              {(sectionContent.author?.name || sectionContent.author?.description) && <_StyledFigcaption>
                  {sectionContent.author?.name && <_StyledSpan>{sectionContent.author.name}</_StyledSpan>}
                  {sectionContent.author?.description && <_StyledSpan2 $_css5={[{
                "fontSize": "1.4rem",
                "lineHeight": "1.5",
                "letterSpacing": "0.014000000000000002rem",
                "fontWeight": "400"
              }, themeStyle.label]}>
                      {sectionContent.author.description}
                    </_StyledSpan2>}
                </_StyledFigcaption>}
              <_StyledQuoteIcon2 mode={mode as Mode} />
            </_StyledBlockquote>
          </_StyledFigure>
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledPath = /*#__PURE__*/_styled("path").withConfig({
  componentId: "sc-sf-197vnxl-1"
})`${p => p.$_css}`;
var _StyledSection = _styled2("section").withConfig({
  componentId: "sc-sf-197vnxl-2"
})`${p => p.$_css2}`;
var _StyledWrap = _styled3(Wrap).withConfig({
  componentId: "sc-sf-197vnxl-3"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-197vnxl-4"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledFigure = _styled5("figure").withConfig({
  componentId: "sc-sf-197vnxl-5"
})`${p => p.$_css3}`;
var _StyledBlockquote = _styled6("blockquote").withConfig({
  componentId: "sc-sf-197vnxl-6"
})({
  "position": "relative"
});
var _StyledSanityRichText = _styled8(SanityRichText).withConfig({
  componentId: "sc-sf-197vnxl-7"
})`${p => p.$_css4}`;
var _StyledFigcaption = _styled9("figcaption").withConfig({
  componentId: "sc-sf-197vnxl-8"
})({
  "marginTop": "var(--spacing-lg)",
  "display": "flex",
  "flexWrap": "wrap",
  "alignItems": "baseline",
  "gap": "0.8rem"
});
var _StyledSpan = _styled10("span").withConfig({
  componentId: "sc-sf-197vnxl-9"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500",
  "--tw-text-opacity": "1",
  "color": "rgb(75 46 248 / var(--tw-text-opacity))"
});
var _StyledSpan2 = _styled11("span").withConfig({
  componentId: "sc-sf-197vnxl-10"
})`${p => p.$_css5}`;